import { useEffect, useMemo, useState } from 'react';
import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import { msaApi } from '../../lib/apiClient.js';
import useUserDraft from '../../utils/useUserDraft.js';
import { EXECUTION_COST_CONFIRMATION_MESSAGE } from '../../utils/engineConfig.js';
import { backtestPeriodError, defaultBacktestPeriod } from '../../utils/backtestPeriod.js';

const SIZING_METHODS = [
  { value: 'equal', label: 'Equal weight' },
  { value: 'fixed_fraction', label: 'Fixed fraction' },
  { value: 'confidence', label: 'Confidence-weighted' },
  { value: 'volatility', label: 'Volatility-weighted' },
  { value: 'kelly', label: 'Kelly' },
];

export const DEFAULT_CONFIG = {
  symbols: ['ABBEYBANK.NG'],
  period: defaultBacktestPeriod(),
  capital: 10000,
  maxPositions: 5,
  sizing: 'equal',
  slPct: 5,
  ptPct: 10,
  maxHoldBars: 60,
  compute: 'cpu',
  entryFill: 'next_open', commissionBps: 10, slippageBps: 5, fixedFee: 0, costsAccepted: false,
};

const EXCHANGES = ['ALL', 'NGX', 'NSE', 'JSE', 'GSE', 'EGX', 'BRVM'];

/**
 * Common ConfigModel-shaped panel shared across every mode tab: universe /
 * symbols (the REAL MyStocks Africa stock list, fetched live with a mock
 * fallback), search + exchange filter, save/load named symbol lists
 * (localStorage), period, capital, sizing, sl/pt/max-hold, and the CPU/GPU
 * compute toggle.
 */
export default function ConfigPanel({ config, onChange, allowGpu = false }) {
  const [universe, setUniverse] = useState(null);
  const [search, setSearch] = useState('');
  const [exchange, setExchange] = useState('ALL');
  const [listName, setListName] = useState('');
  const [savedLists, setSavedLists] = useUserDraft('universe-lists', {});
  const [loadError, setLoadError] = useState('');
  const periodError = backtestPeriodError(config.period);

  useEffect(() => {
    let alive = true;
    msaApi
      .universe()
      .then((u) => alive && setUniverse(Array.isArray(u) ? u : u.items || []))
      .catch(() => { if (alive) { setUniverse([]); setLoadError('Universe unavailable. Check the market-data connection.'); } });
    return () => {
      alive = false;
    };
  }, []);

  function set(key, value) {
    onChange({ ...config, [key]: value });
  }
  function setPeriod(key, value) {
    onChange({ ...config, period: { ...config.period, [key]: value } });
  }
  function toggleSymbol(symbol) {
    const has = config.symbols.includes(symbol);
    set('symbols', has ? config.symbols.filter((s) => s !== symbol) : [...config.symbols, symbol]);
  }

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (universe || [])
      .filter((s) => (exchange === 'ALL' ? true : s.exchange === exchange))
      .filter((s) => !q || s.symbol.toLowerCase().includes(q) || (s.name || '').toLowerCase().includes(q));
  }, [universe, search, exchange]);

  const exchangeCounts = useMemo(() => {
    const counts = {};
    for (const s of universe || []) counts[s.exchange] = (counts[s.exchange] || 0) + 1;
    return counts;
  }, [universe]);

  function selectAllVisible() {
    const symbols = [...new Set(visible.map((s) => s.symbol))];
    set('symbols', symbols);
  }
  function clearSymbols() {
    set('symbols', []);
  }

  function saveCurrentList() {
    const name = listName.trim();
    if (!name) return;
    if (!config.symbols.length) return;
    const lists = { ...savedLists, [name]: config.symbols };
    setSavedLists(lists);
    setListName('');
  }
  function deleteSavedList(name) {
    const lists = { ...savedLists };
    delete lists[name];
    setSavedLists(lists);
  }
  function loadSavedList(name) {
    const symbols = savedLists[name];
    if (symbols) set('symbols', symbols);
  }

  return (
    <Card>
      <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
        Configuration
      </h3>
      {loadError && <p role="alert">{loadError}</p>}
      <p className="aq-note">Symbol history is validated for the selected range before compute credits are reserved.</p>

      <div className="aq-field" style={{ marginBottom: 10 }}>
        <span>
          Universe / symbols ({config.symbols.length} selected ·{' '}
          {universe ? `${universe.length} stocks loaded` : 'loading…'})
        </span>

        <div className="aq-universe-tools">
          <input
            type="search"
            placeholder="Search symbol or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={exchange} onChange={(e) => setExchange(e.target.value)}>
            {EXCHANGES.map((x) => (
              <option key={x} value={x}>
                {x === 'ALL' ? 'All exchanges' : `${x} (${exchangeCounts[x] || 0})`}
              </option>
            ))}
          </select>
          <Button size="sm" onClick={selectAllVisible}>
            Replace selection ({visible.length})
          </Button>
          <Button size="sm" variant="ghost" onClick={clearSymbols}>
            Clear
          </Button>
        </div>

        <div className="aq-symbol-picker">
          {visible.length === 0 && <span className="aq-module-desc">No stocks match.</span>}
          {visible.map((s) => (
            <label key={s.symbol} className="aq-symbol-chip" title={s.name || s.symbol}>
              <input type="checkbox" checked={config.symbols.includes(s.symbol)} onChange={() => toggleSymbol(s.symbol)} />
              <span>
                {s.symbol} <span className="aq-holding-ex">{s.exchange}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="aq-universe-lists">
        <span className="aq-module-desc">Saved lists</span>
        <div className="aq-universe-tools">
          <input
            type="text"
            placeholder="List name, then Save"
            value={listName}
            onChange={(e) => setListName(e.target.value)}
            disabled={config.symbols.length === 0}
          />
          <Button size="sm" onClick={saveCurrentList} disabled={!listName.trim() || config.symbols.length === 0}>
            Save list
          </Button>
        </div>
        {Object.keys(savedLists).length === 0 ? (
          <div className="aq-module-desc">No saved lists yet.</div>
        ) : (
          <div className="aq-saved-list-row">
            {Object.entries(savedLists).map(([name, symbols]) => (
              <span key={name} className="aq-saved-list-chip">
                <button type="button" className="aq-saved-list-name" onClick={() => loadSavedList(name)} title={symbols.join(', ')}>
                  {name} ({symbols.length})
                </button>
                <button type="button" className="aq-holding-remove" onClick={() => deleteSavedList(name)} aria-label={`Delete ${name}`}>
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="aq-bt-params">
        <label className="aq-field">
          <span>From</span>
          <input type="date" value={config.period.from} max={config.period.to || undefined} onChange={(e) => setPeriod('from', e.target.value)} aria-invalid={Boolean(periodError)} />
        </label>
        <label className="aq-field">
          <span>To</span>
          <input type="date" value={config.period.to} min={config.period.from || undefined} onChange={(e) => setPeriod('to', e.target.value)} aria-invalid={Boolean(periodError)} />
        </label>
        <label className="aq-field">
          <span>Capital</span>
          <input type="number" min="1" value={config.capital} onChange={(e) => set('capital', Number(e.target.value))} />
        </label>
        <label className="aq-field">
          <span>Max positions</span>
          <input type="number" value={config.maxPositions} onChange={(e) => set('maxPositions', Number(e.target.value))} />
        </label>
        <label className="aq-field">
          <span>Sizing method</span>
          <select value={config.sizing} onChange={(e) => set('sizing', e.target.value)}>
            {SIZING_METHODS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="aq-field">
          <span>Stop-loss %</span>
          <input type="number" value={config.slPct} onChange={(e) => set('slPct', Number(e.target.value))} />
        </label>
        <label className="aq-field">
          <span>Profit-target %</span>
          <input type="number" value={config.ptPct} onChange={(e) => set('ptPct', Number(e.target.value))} />
        </label>
        <label className="aq-field">
          <span>Max hold (bars)</span>
          <input type="number" value={config.maxHoldBars} onChange={(e) => set('maxHoldBars', Number(e.target.value))} />
        </label>
      </div>
      {periodError && <p className="aq-note aq-note--error" role="alert">{periodError} Backtest submission is disabled.</p>}

      <div className="aq-compute-toggle">
        <fieldset>
          <legend>Execution assumptions</legend>
          <p className="aq-note">Cash-long account, no borrowing. Use one listing currency per run; FX conversion is not enabled.</p>
          <label className="aq-field">Entry fill<select value={config.entryFill || 'next_open'} onChange={(e) => set('entryFill', e.target.value)}>
            <option value="next_open">Next available open</option><option value="close">Signal close (idealized)</option>
          </select></label>
          <label className="aq-field">Commission per side (basis points)<input type="number" min="0" max="9999" value={config.commissionBps ?? 10} onChange={(e) => onChange({ ...config, commissionBps: Number(e.target.value), costsAccepted: false })} /></label>
          <label className="aq-field">Slippage per side (basis points)<input type="number" min="0" max="9999" value={config.slippageBps ?? 5} onChange={(e) => onChange({ ...config, slippageBps: Number(e.target.value), costsAccepted: false })} /></label>
          <label className="aq-field">Fixed fee per order (listing currency)<input type="number" min="0" value={config.fixedFee ?? 0} onChange={(e) => onChange({ ...config, fixedFee: Number(e.target.value), costsAccepted: false })} /></label>
          <label><input type="checkbox" checked={Boolean(config.costsAccepted)} onChange={(e) => set('costsAccepted', e.target.checked)} /> I confirm these research assumptions; they are not verified market tariffs.</label>
          {!config.costsAccepted && <p className="aq-note aq-note--error" role="alert">Required: {EXECUTION_COST_CONFIRMATION_MESSAGE}</p>}
        </fieldset>
        <span>Compute</span>
        <div className="aq-seg">
          <button
            className={`aq-seg-btn ${config.compute === 'cpu' ? 'aq-seg-btn--active' : ''}`}
            onClick={() => set('compute', 'cpu')}
          >
            CPU
          </button>
          <button
            className={`aq-seg-btn ${config.compute === 'gpu' ? 'aq-seg-btn--active' : ''}`}
            disabled={!allowGpu}
            onClick={() => set('compute', 'gpu')}
          >
            GPU
          </button>
        </div>
        {!allowGpu && <p className="aq-note">Rules and portfolio analysis execute on CPU. GPU is available for supported ML research.</p>}
      </div>
    </Card>
  );
}
